import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      if (storedUser && storedToken) {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
      }
    } catch (e) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback((userData, authToken) => {
    if (!userData || !authToken) throw new Error('Invalid user data or token');
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', authToken);
    setUser(userData);
    setToken(authToken);
    return true;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setToken(null);
    return true;
  }, []);

  const updateUser = useCallback((userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return true;
  }, []);

  const isAdmin = useCallback(() => user?.role === 'admin', [user]);
  const isUser  = useCallback(() => user && ['distributor', 'retailer', 'partner'].includes(user.role), [user]);
  const isAuthenticated = useCallback(() => !!(user && token), [user, token]);
  const isActive = useCallback(() => user?.account_status === 'active', [user]);

  const value = { user, token, loading, login, logout, updateUser, isAdmin, isUser, isAuthenticated, isActive };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
